/* eslint-disable react/no-array-index-key */
import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { Box, Table, TableContainer, TableHead, TableRow, TableCell, Typography, TableBody, IconButton, Chip, Tooltip, TextField, useTheme, CircularProgress } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useLazyQuery } from "@apollo/client";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { searchTicket } from "../../redux/action";
import { deleteTicket } from "../../gql/queries/ticket";
import { usePopup } from "../../context/commonPop";
import useQuery from "../../hooks/allQuery";
import TicketAdminUpdateForm from "./TicketUpdateForm";
import { getTicketsAdmin } from "../../gql/queries/ticketadmin";
import DeletePopup from "../tickets/deletePopup";

function TicketAdminListing({ ticketsFetch, setTicketsFetch }) {
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState();
  const [selectedTicket, setSelectedTicket] = useState({});
  const [deleteDisable, setDeleteDisable] = useState(false);
  const [loading, setLoading] = useState(true);

  const [openChild, setOpenChild] = useState(false);
  const theme = useTheme();
  const dispatch = useDispatch();
  const [GET_ALL_TICKETS] = useLazyQuery(getTicketsAdmin, { fetchPolicy: "network-only" });
  const [DELETE_TICKET] = useLazyQuery(deleteTicket, { fetchPolicy: "network-only" });
  const { allQuery } = useQuery();
  const { handleOpen } = usePopup();
  const navigate = useNavigate();
  const currentFilter = useSelector(state => state.ticketReducer.currentFilter);

  const getVisibleTickets = (tickets, filter, ticketSearch) => {
    const searchStr = ticketSearch.toLowerCase().trim();

    const matchesSearch = ticket =>
      Object.keys(ticket).some(key => {
        const value = ticket[key];
        if (value == null) return false;
        return value.toString().toLowerCase().includes(searchStr);
      });

    switch (filter) {
      case "total_tickets":
        return tickets?.filter(ticket => !ticket.isDeleted && matchesSearch(ticket));

      case "Pending":
        return tickets?.filter(ticket => !ticket.isDeleted && ticket.Status === "Pending" && matchesSearch(ticket));

      case "Closed":
        return tickets?.filter(ticket => !ticket.isDeleted && ticket.Status === "Closed" && matchesSearch(ticket));

      case "Open":
        return tickets?.filter(ticket => !ticket.isDeleted && ticket.Status === "Open" && matchesSearch(ticket));

      default:
        throw new Error(`Unknown filter: ${filter}`);
    }
  };

  const tickets = useSelector(state => getVisibleTickets(ticketsFetch, state.ticketReducer.currentFilter, state.ticketReducer.ticketSearch));
  const ticketBadge = ticket =>
    ticket.Status === "Open"
      ? theme.palette.success.light
      : ticket.Status === "Closed"
      ? theme.palette.error.light
      : ticket.Status === "Pending"
      ? theme.palette.warning.light
      : ticket.Status === "Moderate"
      ? theme.palette.primary.light
      : "primary";

  const handleDelete = useCallback(() => {
    setDeleteDisable(true);
    allQuery({
      QUERY: DELETE_TICKET,
      variables: { ticketIsDeleteId: deleteId },
      type: "delete",
      cacheQueryData: {
        cacheQuery: getTicketsAdmin,
      },
      onCompleteFunc: () => {
        setDeleteDisable(false);
        handleOpen(true, "Ticket deleted successfully");
      },
      onErrorFunc: error => {
        console.log("error", error);
        setDeleteDisable(false);
      },
    });
    setOpenChild(false);
  }, [setDeleteDisable, deleteId, getTicketsAdmin, setOpenChild, allQuery, DELETE_TICKET]);

  const handleOpenPopup = id => {
    setDeleteId(id);
    setOpenChild(true);
  };

  useEffect(() => {
    allQuery({
      QUERY: GET_ALL_TICKETS,
      onCompleteFunc: ({ getTickets }) => {
        setTicketsFetch(getTickets);
        setLoading(false);
      },
      onErrorFunc: err => {
        console.log(err);
        setLoading(false);
      },
    });
  }, []);
  return loading ? (
    <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }} mt={4}>
      <CircularProgress size={60} />
    </Box>
  ) : (
    <Box>
      <Box sx={{ maxWidth: "260px", ml: "auto", mt: "32px" }} mb={3}>
        <TextField size="small" label="Search by description or Ticket ID" fullWidth onChange={e => dispatch(searchTicket(e.target.value))} />
      </Box>
      {tickets.length > 0 ? (
        <Box mt={4}>
          <DeletePopup open={openChild} setOpen={setOpenChild} handleDelete={handleDelete} msg="ticket" />
          {/* <Box sx={{ maxWidth: "260px", ml: "auto" }} mb={3}>
        <TextField size="small" label="Search by description or Ticket ID" fullWidth onChange={e => dispatch(searchTicket(e.target.value))} />
      </Box> */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="h6">Ticket Id</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">Description</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">Source</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">Image</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">Status</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">Date</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="h6">Action</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets?.map(ticket => (
                  <TableRow key={ticket.ticketId} hover>
                    <TableCell sx={{ color: theme.palette.primary.main }}>
                      <Box
                        onClick={() => {
                          navigate(`/tickets/chat/${ticket.ticketId}`);
                        }}
                        sx={{ width: "max-content", cursor: "pointer" }}
                      >
                        {ticket.ticketId}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="h6" fontWeight={600} noWrap>
                          {ticket.type}
                        </Typography>
                        <Typography color="textSecondary" noWrap sx={{ maxWidth: "250px" }} variant="subtitle2" fontWeight={400}>
                          {ticket.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle1">{ticket.isDesktop ? "Desktop App" : "Web App"}</Typography>
                    </TableCell>
                    <TableCell>
                      {ticket?.images?.length > 0 ? (
                        ticket.images
                          .slice(0, 2)
                          .map((img, index) => <img key={index} src={img} alt="not found" width={35} height={35} style={{ marginRight: "5px", borderRadius: "5px" }} />)
                      ) : (
                        <span>No Image</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        sx={{
                          backgroundColor: ticketBadge(ticket),
                        }}
                        size="small"
                        label={ticket.Status}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle1">{format(new Date(ticket.createdAt), "E, MMM d")}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit Ticket">
                        <IconButton
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setOpen(true);
                          }}
                        >
                          <EditIcon size="18" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Ticket">
                        <IconButton
                          disabled={deleteDisable}
                          onClick={() => {
                            handleOpenPopup(ticket.ticketId);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TicketAdminUpdateForm open={open} setOpen={setOpen} selectedTicket={selectedTicket} />
        </Box>
      ) : currentFilter === "Pending" ? (
        <Box sx={{ padding: "100px 0px 75px" }}>
          <Typography sx={{ textAlign: "center", fontSize: "16px", fontWeight: "900" }}>Your ticket list is empty.</Typography>
        </Box>
      ) : currentFilter === "Closed" ? (
        <Box sx={{ padding: "100px 0px 75px" }}>
          <Typography sx={{ textAlign: "center", fontSize: "16px", fontWeight: "900" }}>Your ticket list is empty.</Typography>
        </Box>
      ) : (
        <Box sx={{ padding: "100px 0px 75px" }}>
          <Typography sx={{ textAlign: "center", fontSize: "16px", fontWeight: "900" }}>It looks like there are no tickets matching your search.</Typography>
        </Box>
      )}
    </Box>
  );
}

export default TicketAdminListing;
